import { gql } from '@apollo/client'
import { client } from 'App'
import { produce } from 'immer'
import { BulkUserJob, LISTEN_BULK_USER_JOB_CHANGES } from 'mutations/bulk_user_job'
import { GET_COMPANY_USERS, GET_ORGANIZATION_USERS } from 'models/user'

/**
 * Tracks running bulk suspend/enable jobs in a client-only cache list so the global
 * progress pill can show them wherever the admin navigates. Mirrors
 * subscriptions/fileImport.ts — same shape, same lifecycle.
 */
export const GET_BULK_USER_JOBS_LIST = gql`
  query GetBulkUserJobsList {
    bulkUserJobsList @client {
      id
      organizationId
      companyId
      action
      status
      total
      processed
      progress
      counts { disabled enabled skipped failed }
      createdAt
      updatedAt
      finishedAt
    }
  }
`

// Live subscriptions by job id, so dismissing a running job can stop its pushes.
// Without this the row is deleted from the cache and the next push re-inserts it.
const subscriptions: {[jobId: string]: {unsubscribe: () => void}} = {}

// Refetches the lists the job rewrote. The engagement-summary rows behind them are
// updated by the job, so they are stale until this runs.
const refetchUserLists = () => {
  client.refetchQueries({
    include: [GET_ORGANIZATION_USERS, GET_COMPANY_USERS],
  }).catch(err => console.error(err))
}

const upsertJob = (job: Partial<BulkUserJob> & {id: string}) => {
  const cached = client.readQuery<{bulkUserJobsList: BulkUserJob[]}>({
    query: GET_BULK_USER_JOBS_LIST,
  })
  client.writeQuery({
    query: GET_BULK_USER_JOBS_LIST,
    data: produce(cached || {bulkUserJobsList: []}, (draft: any) => {
      const i = draft.bulkUserJobsList.findIndex((j: BulkUserJob) => j.id === job.id)
      const row = {__typename: 'BulkUserJobList', ...job}
      if (i === -1) {
        draft.bulkUserJobsList.unshift(row)
      } else {
        draft.bulkUserJobsList[i] = {...draft.bulkUserJobsList[i], ...row}
      }
    }),
  })
}

export const removeBulkUserJob = (jobId: string) => {
  // Stop the subscription first: deleting the row alone leaves it live, and its next
  // progress push unshifts the row straight back in.
  subscriptions[jobId]?.unsubscribe()
  delete subscriptions[jobId]

  const cached = client.readQuery<{bulkUserJobsList: BulkUserJob[]}>({
    query: GET_BULK_USER_JOBS_LIST,
  })
  if (!cached) return
  client.writeQuery({
    query: GET_BULK_USER_JOBS_LIST,
    data: {bulkUserJobsList: cached.bulkUserJobsList.filter(j => j.id !== jobId)},
  })
}

/**
 * Adds a job to the global list and follows it to completion. Safe to call for a job
 * already being watched — the upsert is keyed by id.
 *
 * The subscription is owned here rather than by a component, so navigating away (or
 * closing the result modal) does not stop progress being tracked.
 */
export const addBulkUserJob = (job: BulkUserJob) => {
  upsertJob(job)
  if (job.status === 'done') {
    // Small selections can finish before we ever subscribe; the lists still need
    // refetching, which otherwise only happens on the subscription's done event.
    refetchUserLists()
    return
  }

  const subscription = client.subscribe<{job: BulkUserJob}>({
    query: LISTEN_BULK_USER_JOB_CHANGES,
    variables: {jobId: job.id},
  }).subscribe({
    next: ({data}) => {
      if (!data?.job) return
      upsertJob({...data.job, id: job.id})
      if (data.job.status === 'done') {
        subscription.unsubscribe()
        delete subscriptions[job.id]
        // Refetching here rather than from the hook means it happens even when the
        // admin closed the modal or navigated away — the hook's own subscription
        // dies with its component, this one does not.
        refetchUserLists()
      }
    },
    error: (err) => {
      console.error(err)
      subscription.unsubscribe()
      delete subscriptions[job.id]
    },
  })

  subscriptions[job.id] = subscription
}
